import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Users, Trophy, Calendar, Star, Shield, Heart, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Layout } from "@/components/layout";
import { SectionHeader, ScrollReveal, PremiumCTA } from "@/components/premium";
import MobileCTA from "@/components/MobileCTA";
import AEOAnswerBlock from "@/components/AEOAnswerBlock";
import { useHomepageContent, DEFAULT_HOMEPAGE_CONTENT } from "@/hooks/useHomepageContent";
const AnnouncementBanner = lazy(() => import("@/components/AnnouncementBanner").then(m => ({ default: m.AnnouncementBanner })));

const GalleryPreview = lazy(() => import("@/components/GalleryPreview"));
const SponsorCarousel = lazy(() => import("@/components/SponsorCarousel"));
const SponsorSpotlight = lazy(() => import("@/components/SponsorSpotlight"));

const divisions = [
  {
    name: "Pre-K – 2nd Grade",
    ageRange: "Ages 4-8",
    description: "Introduction to flag football fundamentals with emphasis on fun and basic skills.",
    icon: Star,
    number: "01",
  },
  {
    name: "3rd – 6th Grade",
    ageRange: "Ages 8-12",
    description: "Developing teamwork and game strategy while building athletic skills.",
    icon: Shield,
    number: "02",
  },
  {
    name: "7th – 9th Grade",
    ageRange: "Ages 12-15",
    description: "Competitive play with advanced techniques and leadership development.",
    icon: Trophy,
    number: "03",
  },
];

const features = [
  {
    icon: Users,
    title: "Teamwork",
    description: "Learn to work together as a team and build lasting friendships.",
  },
  {
    icon: Trophy,
    title: "Sportsmanship",
    description: "Win with grace, lose with dignity, and always play fair.",
  },
  {
    icon: Heart,
    title: "Healthy Lifestyles",
    description: "Promote physical fitness and healthy habits for life.",
  },
];

const quickLinks = [
  { to: "/schedules", icon: Calendar, title: "Schedules", description: "View game schedules and important dates" },
  { to: "/standings", icon: Trophy, title: "Standings", description: "Check current team standings by division" },
  { to: "/league-organizing", icon: Users, title: "For Coaches", description: "Access playbooks, practice plans, and more" },
  { to: "/my-team", icon: Heart, title: "Find My Player", description: "Search for your player in the Parent Portal to see team info & schedules" },
];

const faqs = [
  {
    question: "What age groups can play flag football in Bad Axe?",
    answer: "Back to The Roots Flag Football offers three divisions for youth ages 4 through 15: Pre-K to 2nd grade (ages 4-8), 3rd to 6th grade (ages 8-12), and 7th to 9th grade (ages 12-15). All skill levels are welcome.",
  },
  {
    question: "When is the flag football season in Bad Axe, Michigan?",
    answer: "Our spring season typically runs from late April through late June with games on Saturdays. Registration dates vary by season, so check our schedules and registration pages for current information.",
  },
  {
    question: "How much does it cost to register for youth flag football?",
    answer: "Registration is $50 per player, which includes a jersey, flags, and the full season of games. Financial assistance may be available; contact the league directly for current options.",
  },
  {
    question: "Where are flag football games played in Bad Axe?",
    answer: "Games are played at the Youth Sports Complex at West Irwin Street and North Outer Drive in Bad Axe, Michigan. The complex has multiple fields for games across our three age divisions.",
  },
  {
    question: "How many downs do teams get in flag football?",
    answer: "Teams get 3 downs to advance the ball to midfield, then 3 more downs to score a touchdown. If the offense fails on 3rd down, the other team takes over at the spot of the ball.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer,
    },
  })),
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "EventSeries",
  "name": "Back to The Roots Flag Football — Fall 2026 Season",
  "description": "Youth flag football season in Bad Axe, Michigan for ages 4-15. Games are played at the Youth Sports Complex.",
  "url": "https://backtotherootsflagfootball.org/schedules",
  "location": {
    "@type": "Place",
    "name": "Bad Axe Youth Sports Complex",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Bad Axe",
      "addressRegion": "MI",
      "postalCode": "48413",
      "addressCountry": "US"
    }
  },
  "organizer": {
    "@type": "SportsOrganization",
    "name": "Back to The Roots Flag Football",
    "url": "https://backtotherootsflagfootball.org"
  }
};

const Index = () => {
  const { data: content = DEFAULT_HOMEPAGE_CONTENT } = useHomepageContent();
  return (
    <Layout>
      <Helmet>
        <link rel="canonical" href="https://backtotherootsflagfootball.org/" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
      </Helmet>
      {/* Hero Section - Full Screen with Dark Gradient */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Dark Gradient Background */}
        <div className="absolute inset-0 bg-hero-premium" />

        {/* Brand Motif Overlay — diagonal field lines */}
        <div className="absolute inset-0 hero-brand-motif" />

        <div className="container relative z-10 py-20">
          <div className="max-w-4xl mx-auto text-center text-primary-foreground">
            {/* Premium Badge */}
            {content.badge.enabled && (
              <Link
                to={content.badge.href}
                className="inline-flex items-center gap-2 bg-secondary/40 backdrop-blur-sm border border-secondary/30 text-primary-foreground px-5 py-2 rounded-full text-sm font-medium mb-8 animate-slide-up hover:bg-secondary/60 transition-colors cursor-pointer"
              >
                <span className="w-2 h-2 bg-secondary rounded-full animate-pulse" />
                {content.badge.text}
              </Link>
            )}

            {/* Main Heading */}
            <div className="space-y-2 mb-8">
              <p
                className="text-lg md:text-xl tracking-[0.3em] uppercase text-primary-foreground/60 font-light animate-slide-up"
                style={{ animationDelay: "0.1s" }}
              >
                {content.hero.eyebrow}
              </p>
              <h1
                className="font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight animate-slide-up text-shadow-hero"
                style={{ animationDelay: "0.2s" }}
              >
                <span className="text-secondary italic">{content.hero.title}</span>
              </h1>
              <p
                className="text-2xl md:text-3xl tracking-[0.2em] uppercase font-light animate-slide-up"
                style={{ animationDelay: "0.3s" }}
              >
                {content.hero.subtitle}
              </p>
            </div>

            {/* Decorative Line */}
            <div
              className="flex items-center justify-center gap-4 mb-8 animate-slide-up"
              style={{ animationDelay: "0.4s" }}
            >
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-primary-foreground/30 to-transparent" />
              <div className="w-2 h-2 rotate-45 border border-secondary/50" />
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-primary-foreground/30 to-transparent" />
            </div>

            {/* Description */}
            <p
              className="text-lg md:text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto leading-relaxed animate-slide-up"
              style={{ animationDelay: "0.5s" }}
            >
              {content.hero.description}
            </p>

            {/* CTA Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up"
              style={{ animationDelay: "0.6s" }}
            >
              {content.primaryCta.enabled && (
                <Button
                  asChild
                  size="lg"
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold px-8 py-6 text-base btn-glow shadow-glow-sm hover:shadow-glow transition-shadow duration-300"
                >
                  <Link to={content.primaryCta.href}>
                    {content.primaryCta.label}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              )}
              {content.secondaryCta.enabled && (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground/50 px-8 py-6 text-base transition-all duration-300"
                >
                  <Link to={content.secondaryCta.href}>{content.secondaryCta.label}</Link>
                </Button>
              )}
              {content.tertiaryCta.enabled && (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-secondary/40 text-secondary hover:bg-secondary/10 hover:border-secondary/60 px-8 py-6 text-base transition-all duration-300"
                >
                  <Link to={content.tertiaryCta.href}>
                    {content.tertiaryCta.label}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-primary-foreground/50 animate-scroll-hint">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <ChevronDown className="h-5 w-5" />
        </div>
      </section>

      {/* Announcements Banner */}
      <Suspense fallback={null}>
        <section className="py-6 bg-section-primary">
          <div className="container">
            <AnnouncementBanner />
          </div>
        </section>
      </Suspense>

      {/* Divisions Section */}
      <section className="relative py-24 md:py-32 bg-section-highlight bg-field-subtle section-ambient-orbs" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 800px' }}>
        <div className="container">
          <SectionHeader
            label="Three Age Groups"
            title="Our Divisions"
            description="Find your level and join the fun"
          />

          {/* Division Cards */}
          <div className="grid md:grid-cols-3 gap-8">
            {divisions.map((division, index) => (
              <ScrollReveal
                key={division.name} 
                animation="fade-up"
                delay={index * 100}
              >
                <Card className="relative overflow-hidden h-full glass-card border-gradient hover:shadow-premium-hover transition-all duration-500 hover:-translate-y-2 group">
                  {/* Number Badge */}
                  <div className="absolute top-4 right-4 text-6xl font-display font-bold text-accent/10 group-hover:text-accent/20 transition-colors">
                    {division.number}
                  </div>
                  
                  <CardHeader className="relative z-10">
                    <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 border border-accent/20">
                      <division.icon className="h-7 w-7 text-accent" />
                    </div>
                    <CardTitle className="text-xl font-semibold">{division.name}</CardTitle>
                    <p className="text-accent font-medium tracking-wide">{division.ageRange}</p>
                  </CardHeader>
                  
                  <CardContent className="relative z-10">
                    <p className="text-muted-foreground leading-relaxed">{division.description}</p>
                  </CardContent>

                  {/* Hover Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Mission/Values Section */}
      <section className="relative py-24 md:py-32 bg-section-warm section-fade-bridge" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 700px' }}>
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <ScrollReveal animation="slide-right">
              <div>
                <p className="text-sm tracking-[0.3em] uppercase text-accent mb-4">Why We Play</p>
                <h2 className="font-display text-4xl md:text-5xl font-bold mb-8">Our Mission</h2>
                
                {/* Elegant Quote */}
                <div className="relative mb-8 pl-6 border-l-2 border-accent/30">
                  <p className="text-xl text-foreground/80 italic leading-relaxed">
                    "Getting back to the roots of youth sports – focusing on fun, 
                    fundamentals, and friendship rather than just winning."
                  </p>
                </div>

                <p className="text-muted-foreground mb-8 leading-relaxed">
                  Bad Axe Back to the Roots Flag Football is dedicated to providing a positive, 
                  inclusive environment where young athletes can learn the fundamentals of football 
                  while developing valuable life skills.
                </p>
                
                <Button asChild className="group">
                  <Link to="/about">
                    About Our League
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </ScrollReveal>

            {/* Feature Cards */}
            <div className="space-y-4">
              {features.map((feature, index) => (
                <ScrollReveal
                  key={feature.title} 
                  animation="slide-left"
                  delay={index * 100}
                >
                  <div className="group flex gap-5 p-6 bg-background rounded-xl shadow-sm hover:shadow-premium transition-all duration-300 hover:-translate-y-1 border border-border/50">
                    <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 border border-accent/20">
                      <feature.icon className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Preview */}
      <Suspense fallback={null}>
        <GalleryPreview />
      </Suspense>

      {/* Quick Links */}
      <section className="relative py-24 md:py-32 bg-section-primary section-edge-glow" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 600px' }}>
        <div className="container">
          <SectionHeader
            label="Quick Access"
            title="Explore More"
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <ScrollReveal
                key={link.to}
                animation="fade-up"
                delay={index * 100}
              >
                <Link 
                  to={link.to} 
                  className="group block h-full"
                >
                  <Card className="h-full hover:shadow-premium transition-all duration-500 hover:-translate-y-2 border-border/50 group-hover:border-accent/30">
                    <CardHeader className="pb-3">
                      <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 border border-accent/20">
                        <link.icon className="h-6 w-6 text-accent" />
                      </div>
                      <CardTitle className="text-lg group-hover:text-accent transition-colors">{link.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{link.description}</p>
                    </CardContent>
                  </Card>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Using PremiumCTA */}
      {content.bottomBanner.enabled && (
        <PremiumCTA
          title={content.bottomBanner.title}
          description={content.bottomBanner.description}
          primaryAction={{
            label: content.bottomBanner.primaryLabel,
            href: content.bottomBanner.primaryHref,
          }}
          secondaryAction={{
            label: content.bottomBanner.secondaryLabel,
            href: content.bottomBanner.secondaryHref,
          }}
          variant="gradient"
        />
      )}

      {/* FAQ Section for AEO */}
      <section className="relative py-24 md:py-32 bg-section-highlight" style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 800px' }}>
        <div className="container">
          <SectionHeader
            label="Common Questions"
            title="Frequently Asked Questions"
            description="Everything you need to know about youth flag football in Bad Axe"
          />

          <div className="max-w-3xl mx-auto mb-12">
            <AEOAnswerBlock
              question="What is Back to The Roots Youth Flag Football in Bad Axe, Michigan?"
              answer="Back to The Roots Flag Football is Huron County's premier youth flag football league operating in Bad Axe, MI. Serving boys and girls ages 4 to 15 across three age divisions, the league emphasizes sportsmanship, teamwork, character development, and healthy physical fitness at the Youth Sports Complex."
              badge="Google AI Overview Summary"
            />
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 80}>
                <div className="p-6 bg-background rounded-xl border border-border/50 shadow-sm">
                  <h2 className="text-lg font-semibold mb-3">{faq.question}</h2>
                  <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsor Spotlight */}
      <Suspense fallback={null}>
        <SponsorSpotlight />
      </Suspense>

      {/* Sponsors Section */}
      <Suspense fallback={null}>
        <SponsorCarousel />
      </Suspense>

      {/* FAQPage JSON-LD */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <MobileCTA />
    </Layout>
  );
};

export default Index;
